/**
 * @file   src\Pages\ManageCarWash.tsx
 * @brief  ManageCarWash page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Table, Link, Button, useOutletContext, useNavigate, useEffect, useState, useLocation, Pagination, Tooltip, OverlayTrigger, React, Modal, Badge } from '../utils/thirdpartyComponents';
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import CWSearchinput from '../components/CWSearchinput';
import AddIcon from '../assets/images/icons/addicon';
import { AppContextType, CompanyItem } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import SortingIcon from '../assets/images/icons/sorting';
import axios from '../config/axiosConfig';
import { CarWashApi } from '../utils/apiUrls';
import SmallLoader from '../components/SmallLoader';
import { formatDate, getFromLocalStorage } from '../utils/helper';
import { MessageToaster } from '../utils/toastUtil';
const ManageCarWash = () => {
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    // To get path from which redirection was done
    const location = useLocation();
    const origin = location.state?.origin || 'Unknown';
    const storageData = getFromLocalStorage('CARWASH_ADMIN_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;
    const query = new URLSearchParams(location.search);
    const toaster = new MessageToaster();

    // State to display company details
    const [companyDetails, setCompanyDetails] = useState<CompanyItem[]>([])
    // State to handle search
    const [searchValue, setSearchValue] = useState<string>(query.get('search') || '')
    const [loading, setLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState<string | null>(query.get('sortOrder') || null);
    const [currentPage, setCurrentPage] = useState(query.get('page') ? parseInt(query.get('page') as string, 10) : 1);
    const [totalPages, setTotalPages] = useState(1);
    const [showCarwashDeletePopup, setCarwashDeletePopup] = useState<boolean>(false);
    const [selected_carwash_for_delete, setSelected_carwash_for_delete] = useState<any>(0);
    const [selected_carwash_name_for_delete, setSelected_carwash_name_for_delete] = useState<any>('');

    useEffect(() => {
        localStorage.removeItem('bankInformation');

        // To display heading
        handleUpdate()

        // Get Companies
        getCompanies(currentPage)

    }, [currentPage, searchValue, sortOrder])


    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Manage Car Wash Companies", url: `/superadmin/${pageURLs.dashboard}`, showBackButton: origin === "MainMenu" || (userInfo?.userTypeId == 2 && userInfo?.permissionType == 2) ? false : true, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    //Get company list
    const getCompanies = async (pageNo = 1) => {
        try {
            let param;

            if (searchValue.length) {
                param = `?pageNo=${pageNo}&q=${searchValue}`
            } else {
                param = `?pageNo=${pageNo}`;
            }

            if (sortOrder) {
                param = param + `&sortOrder=${sortOrder}&sort=CAR_WASH_NAME`
            }

            const { data } = await axios.get(CarWashApi.GET_COMPANIES + param);
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const company_list: CompanyItem[] = data.data.list;
                // Check if location_list is empty
                if (company_list.length > 0) {
                    setCompanyDetails(company_list);
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Company list is empty');
                    setCompanyDetails([]);  // Set to an empty array if location_list is empty
                }
            } else {
                console.log('Company list is not available or is not an array');
                setCompanyDetails([]);  // Set to an empty array if data is not available
            }

        } catch (error: any) {
            console.error('Error fetching company list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
        finally {
            setLoading(false); // Hide loader
        }
    }

    // Triggers when input is entered in search
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);
        setCurrentPage(1)
    }

    // Function to sort data
    const sortByName = () => {

        setSortOrder(sortOrder === null ? 'asc' : sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const handleClearSearch = () => {
        setSearchValue('');
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };
    // Truncate text function to limit the length of the address displayed
    const truncateText = (text: string, maxLength: number): string => {
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Render tooltip function for the full address
    const renderTooltip = (fullAddress: string) => {
        const TooltipComponent = (props: any) => (
            <Tooltip {...props}>{fullAddress}</Tooltip>
        );
        TooltipComponent.displayName = `Tooltip(${fullAddress})`; // Dynamic display name
        return TooltipComponent;
    };


     //Hide ppopup
     const hideCarwashDeleteModal = () => {
        setCarwashDeletePopup(false);
    }
    //Show modal popup

    const DeletebuttonClick = (carwash_id: number | undefined, delete_carwash_name: string | undefined | null) => {
        setSelected_carwash_for_delete(carwash_id);
        setSelected_carwash_name_for_delete(delete_carwash_name);
        setCarwashDeletePopup(true);
    }

    //Delete action
    const deleteCarwash = async () => {
            try {
                setLoading(true)
                const carwashId = selected_carwash_for_delete;
                const { data } = await axios.delete(CarWashApi.DELETE_COMPANY + carwashId);
                setLoading(false)    
                if (data?.error === 0) {
                    toaster.toastSuccess(data?.message);
                    // Reload promotion list
                    hideCarwashDeleteModal()
                    getCompanies(currentPage)
                }
                else {
                    // show error message
                    hideCarwashDeleteModal()
                    toaster.toastError(data.message);
                }
            } catch (error: any) {
                throw new Error(error.response?.data?.message || error.message);
            } finally {
                setLoading(false)
            }
        }


    return (

        <div className='manage-carwash-container'>
            <Container>
                <div className="manage-header-block d-flex justify-content-between align-items-center">
                    <div className="search-wrap">
                        <CWSearchinput
                            id="search"
                            name="search"
                            type="text"
                            placeholder="Search by Car Wash Name, Owner Name"
                            value={searchValue}
                            onChange={handleSearch}
                            onClose={handleClearSearch}

                        />
                    </div>
                    <Button
                        title='Add New Car Wash'
                        type="submit"
                        variant="primary"
                        className="primary-btn d-flex align-items-center"
                        onClick={() => navigate(`/superadmin/${pageURLs.add_carwash}`)}
                    >
                        Add New Car Wash
                        <AddIcon />
                    </Button>
                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th className='sort nowrap'
                                    onClick={() => sortByName()}
                                >
                                    Car Wash Name <SortingIcon sortOrder={sortOrder} /></th>
                                <th className='nowrap'>Owner Name</th>
                                <th>Address</th>
                                <th className='nowrap'>Phone Number</th>
                                <th className='nowrap'>Email Address</th>
                                <th>No. of Locations</th>
                                <th>Subscription Paid Date</th>
                                {/* <th className='sort nowrap' onClick={() => sortByName("status")}>Subscription Status <SortingIcon /></th> */}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companyDetails && companyDetails.length ? (
                                companyDetails.map((item, index) => {
                                    const fullAddress = `${item.address1}${item.address2 ? `, ${item.address2}` : ''}, ${item.city}, ${item.state}, ${item.zipCode}`;
                                    const short_address = truncateText(fullAddress, 20)
                                    return (<tr key={index}>
                                        <td><Link to={`/superadmin/${pageURLs.carwash_locations}/${item.carWashID}?fromPage=${currentPage}&search=${searchValue}`} >{item.companyName}</Link>
                                        {item.IsCompanyActive === 0 &&
                                        (
                                        <Badge className='inactive mt-1'>
                                            Deactivated
                                        </Badge>
                                        )}
                                        </td>
                                        <td>{item.contactName}</td>
                                        {/* Address with OverlayTrigger to show full address on hover */}
                                        <td>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={renderTooltip(fullAddress)} // Pass the full address to the tooltip
                                            >
                                                <span>{short_address}</span>
                                            </OverlayTrigger>
                                        </td>

                                        <td className='nowrap'>{item.phoneNumber}</td>
                                        <td className='nowrap'>{item.email}</td>
                                        <td>{item.numberOfLocation}</td>
                                        <td>
                                            {item.CustomerPaymentMethodId || Number(item.countryCode) === 1 ? item.subscriptionStartDate === null ? 'NA' : formatDate(item.subscriptionStartDate.toString()) : <span className="error-msg">Payment info not added</span>}
                                        </td>

                                        <td>
                                            <div className="active-group d-flex align-items-center">
                                                <Button variant="light" title="Edit" className="light-btn action-btn" onClick={() => navigate(`/superadmin/${pageURLs.edit_carwash}/${item.carWashID}`, { state: { page: currentPage, search: searchValue, sortOrder: sortOrder } })}><EditIcon /></Button>
                                                <Button variant="light" title="Delete" className="light-btn action-btn" onClick={() => DeletebuttonClick(item.carWashID, item.companyName)}><DeleteIcon /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                </tr>
                            )}


                        </tbody>
                    </Table>

                    {companyDetails.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}

                </div>
            </Container >
            {loading && <SmallLoader />}

            <Modal
                show={showCarwashDeletePopup}
                onHide={hideCarwashDeleteModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Do you want to delete the car wash &apos;{selected_carwash_name_for_delete}&apos;?</h5>
                    <div className="btn-sec d-grid">
                        <Button variant="light" className="secondary-btn" onClick={hideCarwashDeleteModal}>Not Now</Button>
                        <Button variant="primary" className='primary-btn btn-main m-0' onClick={deleteCarwash}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div >
    )
}


export default ManageCarWash;
